<template>
  <div>
    <div class="filter-container">
      <el-select
        v-model="query.campagne_agricole_id"
        clearable
        class="mr-15 filter-item"
        style="width: 220px;"
        :placeholder="$t('superviseur.aValider.toutesCampagnes')"
        @change="handleFilter"
      >
        <el-option
          v-for="campagne in campagnes"
          :key="campagne.id"
          :label="campagne.nom"
          :value="campagne.id"
        />
      </el-select>

      <el-select
        v-model="query.statut"
        clearable
        class="mr-15 filter-item"
        style="width: 180px;"
        :placeholder="$t('controleInterne.supervision.statut')"
        @change="handleFilter"
      >
        <el-option :label="$t('controleInterne.supervision.enAttente')" value="en_attente" />
        <el-option :label="$t('controleInterne.supervision.approuve')" value="approuve" />
        <el-option :label="$t('controleInterne.supervision.rejete')" value="rejete" />
        <el-option :label="$t('controleInterne.supervision.aCorriger')" value="a_corriger" />
      </el-select>

      <el-input
        v-model="query.keyword"
        :placeholder="$t('general.keyword')"
        class="wd-200 mr-15 filter-item"
        @keyup.enter.native="handleFilter"
      />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        {{ $t('general.search') }}
      </el-button>
    </div>

    <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%">
      <el-table-column align="center" :label="$t('general.code')" min-width="120">
        <template slot-scope="scope">
          <span>{{ scope.row.code }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('producteur.producteur')" min-width="160">
        <template slot-scope="scope">
          <span>{{ scope.row.producteur }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('controleur.controleur')" min-width="140">
        <template slot-scope="scope">
          <span>{{ scope.row.controleur }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('superviseur.aValider.typeModification')" width="110">
        <template slot-scope="scope">
          <el-tag size="mini" type="info">{{ typeLabel(scope.row.modification_type) }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('controleInterne.supervision.statut')" width="130">
        <template slot-scope="scope">
          <el-tag :type="statutTagType(scope.row.statut_supervision)" size="mini">
            {{ statutLabel(scope.row.statut_supervision) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('general.actions')" width="120">
        <template slot-scope="scope">
          <el-button type="primary" size="small" @click="openReview(scope.row)">
            {{ $t('controleInterne.supervision.review') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-if="total > 0"
      :total="total"
      :page.sync="query.page"
      :limit.sync="query.limit"
      @pagination="getList"
    />

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="70%"
      @opened="onDialogOpened"
    >
      <div v-if="selectedRow">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item :label="$t('general.code')">{{ selectedRow.code }}</el-descriptions-item>
          <el-descriptions-item :label="$t('parcelle.parcelle')">{{ selectedRow.parcelle_libelle }}</el-descriptions-item>
          <el-descriptions-item :label="$t('producteur.producteur')">{{ selectedRow.producteur }}</el-descriptions-item>
          <el-descriptions-item :label="$t('controleur.controleur')">{{ selectedRow.controleur }}</el-descriptions-item>
          <el-descriptions-item :label="$t('superviseur.aValider.typeModification')">
            {{ typeLabel(selectedRow.modification_type) }}
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="hasGpsChange" style="margin-top: 16px;">
          <h4>{{ $t('superviseur.aValider.carteGps') }}</h4>
          <gps-compare-map
            v-if="dialogVisible"
            ref="gpsCompareMap"
            :key="mapRenderKey"
            :gps-before="selectedRow.donnees_avant && selectedRow.donnees_avant.gps"
            :gps-after="selectedRow.donnees_apres && selectedRow.donnees_apres.gps"
          />
          <el-collapse style="margin-top: 12px;">
            <el-collapse-item :title="$t('superviseur.aValider.detailsGps')" name="gps-json">
              <p class="gps-json-label">{{ $t('superviseur.aValider.gpsAvant') }}</p>
              <pre class="gps-block">{{ formatGps(selectedRow.donnees_avant && selectedRow.donnees_avant.gps) }}</pre>
              <p class="gps-json-label">{{ $t('superviseur.aValider.gpsApres') }}</p>
              <pre class="gps-block">{{ formatGps(selectedRow.donnees_apres && selectedRow.donnees_apres.gps) }}</pre>
            </el-collapse-item>
          </el-collapse>
        </div>

        <div v-if="otherChanges.length" style="margin-top: 16px;">
          <h4>{{ $t('superviseur.aValider.autresModifications') }}</h4>
          <el-table :data="otherChanges" border size="mini" style="width: 100%;">
            <el-table-column prop="field" :label="$t('superviseur.aValider.champ')" min-width="140" />
            <el-table-column prop="avant" :label="$t('superviseur.aValider.valeurAvant')" min-width="160" />
            <el-table-column prop="apres" :label="$t('superviseur.aValider.valeurApres')" min-width="160" />
          </el-table>
        </div>

        <el-form :model="approbationForm" label-position="top" style="margin-top: 16px;">
          <el-form-item :label="$t('controleInterne.supervision.decision')">
            <el-radio-group v-model="approbationForm.statut">
              <el-radio label="approuve">{{ $t('controleInterne.supervision.approuve') }}</el-radio>
              <el-radio label="a_corriger">{{ $t('controleInterne.supervision.aCorriger') }}</el-radio>
              <el-radio label="rejete">{{ $t('controleInterne.supervision.rejete') }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('controleInterne.supervision.commentaire')">
            <el-input v-model="approbationForm.commentaire" type="textarea" :rows="3" />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('general.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitApprobation">
          {{ $t('general.save') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import GpsCompareMap from '@/components/GpsCompareMap';
import Resource from '@/api/resource';
import waves from '@/directive/waves';
import { getSuperviseurActivites, approuverParcelleModificationSuperviseur } from '@/api/superviseur';

const campagneResource = new Resource('campagne_agricoles');

export default {
  name: 'SuperviseurParcellesModificationsList',
  components: { Pagination, GpsCompareMap },
  directives: { waves },
  data() {
    return {
      list: [],
      campagnes: [],
      total: 0,
      loading: false,
      saving: false,
      dialogVisible: false,
      mapRenderKey: 0,
      selectedRow: null,
      query: {
        page: 1,
        limit: 20,
        keyword: '',
        statut: 'en_attente',
        campagne_agricole_id: null,
        type: 'parcelle',
      },
      approbationForm: {
        statut: 'approuve',
        commentaire: '',
      },
    };
  },
  computed: {
    dialogTitle() {
      if (!this.selectedRow) {
        return '';
      }
      return this.$t('superviseur.aValider.reviewParcelle', { code: this.selectedRow.code });
    },
    hasGpsChange() {
      return this.selectedRow &&
        this.selectedRow.donnees_apres &&
        Object.prototype.hasOwnProperty.call(this.selectedRow.donnees_apres, 'gps');
    },
    otherChanges() {
      if (!this.selectedRow || !this.selectedRow.donnees_apres) {
        return [];
      }
      const avant = this.selectedRow.donnees_avant || {};
      const apres = this.selectedRow.donnees_apres || {};
      return Object.keys(apres)
        .filter(key => key !== 'gps')
        .map(key => ({
          field: key,
          avant: this.formatValue(avant[key]),
          apres: this.formatValue(apres[key]),
        }));
    },
  },
  created() {
    this.loadCampagnes();
  },
  methods: {
    typeLabel(type) {
      const map = {
        gps: this.$t('superviseur.aValider.typeGps'),
        info: this.$t('superviseur.aValider.typeInfo'),
        mixte: this.$t('superviseur.aValider.typeMixte'),
      };
      return map[type] || type;
    },
    statutLabel(statut) {
      const map = {
        en_attente: this.$t('controleInterne.supervision.enAttente'),
        approuve: this.$t('controleInterne.supervision.approuve'),
        rejete: this.$t('controleInterne.supervision.rejete'),
        a_corriger: this.$t('controleInterne.supervision.aCorriger'),
      };
      return map[statut] || statut;
    },
    statutTagType(statut) {
      if (statut === 'approuve') {
        return 'success';
      }
      if (statut === 'rejete') {
        return 'danger';
      }
      if (statut === 'a_corriger') {
        return 'warning';
      }
      return 'info';
    },
    formatGps(gps) {
      if (!gps) {
        return '—';
      }
      try {
        return JSON.stringify(gps, null, 2);
      } catch (error) {
        return String(gps);
      }
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      if (typeof value === 'boolean') {
        return value ? this.$t('general.yes') : this.$t('general.no');
      }
      return String(value);
    },
    async loadCampagnes() {
      const { data } = await campagneResource.list({ limit: 100 });
      this.campagnes = data || [];
      const routeCampagneId = parseInt(this.$route.query.campagne_agricole_id, 10);
      if (routeCampagneId && this.campagnes.some(c => c.id === routeCampagneId)) {
        this.query.campagne_agricole_id = routeCampagneId;
      } else {
        try {
          const { data: current } = await campagneResource.sublist('/en_cours');
          this.query.campagne_agricole_id = current && current.id ? current.id : (this.campagnes[0] ? this.campagnes[0].id : null);
        } catch (error) {
          this.query.campagne_agricole_id = this.campagnes[0] ? this.campagnes[0].id : null;
        }
      }
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const response = await getSuperviseurActivites(this.query);
        this.list = response.data || [];
        this.total = response.meta ? response.meta.total : this.list.length;
      } finally {
        this.loading = false;
      }
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    openReview(row) {
      this.selectedRow = row;
      this.approbationForm = {
        statut: row.approbation_superviseur && row.approbation_superviseur.statut !== 'en_attente'
          ? row.approbation_superviseur.statut
          : 'approuve',
        commentaire: row.approbation_superviseur ? (row.approbation_superviseur.commentaire || '') : '',
      };
      this.mapRenderKey += 1;
      this.dialogVisible = true;
    },
    onDialogOpened() {
      this.$nextTick(() => {
        if (this.$refs.gpsCompareMap && typeof this.$refs.gpsCompareMap.resizeMap === 'function') {
          this.$refs.gpsCompareMap.resizeMap();
        }
      });
    },
    async submitApprobation() {
      if (!this.selectedRow) {
        return;
      }
      this.saving = true;
      try {
        await approuverParcelleModificationSuperviseur(this.selectedRow.id, this.approbationForm);
        this.$message.success(this.$t('controleInterne.supervision.saved'));
        this.dialogVisible = false;
        this.getList();
      } catch (error) {
        console.error(error);
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : this.$t('general.errorOccurred');
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.gps-block {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.gps-json-label {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
}
</style>
